import Layout from '@/components/feature/Layout';
import { companyInfo } from '@/mocks/company';
import { products } from '@/mocks/products';
import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

const specLabels: Record<string, string> = {
  potencia: 'Potência',
  velocidade: 'Velocidade',
  temperatura: 'Temperatura',
  material: 'Material',
};

const specIcons: Record<string, string> = {
  potencia: 'ri-flashlight-line',
  velocidade: 'ri-speed-line',
  temperatura: 'ri-temp-hot-line',
  material: 'ri-stack-line',
};

export default function ModeloDetalhePage() {
  const { slug } = useParams<{ slug: string }>();
  const product = products.find((p) => p.slug === slug);
  const [selectedImage, setSelectedImage] = useState(product?.image || '');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const allImages = product ? [product.image, ...(product.galleryImages || [])] : [];

  useEffect(() => {
    setSelectedImage(product?.image || '');
    setLightboxIndex(null);
  }, [slug, product?.image]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setLightboxIndex(null);
      } else if (e.key === 'ArrowRight') {
        setLightboxIndex((i) => (i === null ? i : (i + 1) % allImages.length));
      } else if (e.key === 'ArrowLeft') {
        setLightboxIndex((i) => (i === null ? i : (i - 1 + allImages.length) % allImages.length));
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, allImages.length]);

  if (!product) {
    return (
      <Layout>
        <div className="w-full px-4 md:px-6 lg:px-10 pt-28 md:pt-32 pb-16 md:pb-24 text-center">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 uppercase mb-4">Modelo não encontrado</h1>
          <p className="text-foreground-500 text-sm mb-8">O modelo que você procura não existe ou foi removido do catálogo.</p>
          <Link
            to="/modelos-de-maquinas"
            className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-bold text-sm px-8 py-4 rounded-full transition-all cursor-pointer whitespace-nowrap uppercase tracking-wide"
          >
            <i className="ri-arrow-left-line" /> Ver todos os modelos
          </Link>
        </div>
      </Layout>
    );
  }

  const related = products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 3);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    image: product.image,
    description: product.description,
    category: product.category,
    brand: { '@type': 'Brand', name: companyInfo.name },
    additionalProperty: [
      { '@type': 'PropertyValue', name: 'Potência', value: product.specs.potencia },
      { '@type': 'PropertyValue', name: 'Velocidade', value: product.specs.velocidade },
      { '@type': 'PropertyValue', name: 'Temperatura', value: product.specs.temperatura },
      { '@type': 'PropertyValue', name: 'Material', value: product.specs.material },
    ],
    inLanguage: 'pt-BR',
  };

  const whatsappMessage = encodeURIComponent(`Olá! Estou no site da Maquinasdesoldageomembrana.com.br e gostaria de informações sobre a máquina ${product.name}.`);
  const whatsappUrl = `https://wa.me/${companyInfo.whatsapp}?text=${whatsappMessage}`;

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <div className="w-full px-4 md:px-6 lg:px-10 pt-28 md:pt-32 pb-16 md:pb-24">
        <nav className="text-xs text-foreground-400 mb-6 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-primary-600 cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <Link to="/modelos-de-maquinas" className="hover:text-primary-600 cursor-pointer">Modelos de Máquinas</Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <span className="text-foreground-600">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-12">
          <div>
            <button type="button" onClick={() => setLightboxIndex(allImages.indexOf(selectedImage))} className="relative w-full h-72 md:h-96 bg-gradient-to-br from-background-100 to-background-200 rounded-2xl flex items-center justify-center p-8 overflow-hidden mb-4 cursor-zoom-in group">
              {product.badge && (
                <span className="absolute top-4 left-4 bg-primary-500 text-white text-[11px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wide z-10">{product.badge}</span>
              )}
              <img alt={`${product.name} - ${product.category.toLowerCase()}`} className="h-full w-full object-contain" src={selectedImage || product.image} />
              <span className="absolute bottom-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                <i className="ri-zoom-in-line text-lg" />
              </span>
            </button>
            {product.galleryImages && product.galleryImages.length > 0 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                <button
                  type="button"
                  onClick={() => { setSelectedImage(product.image); setLightboxIndex(0); }}
                  className={`flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-xl border-2 overflow-hidden cursor-pointer transition-all ${selectedImage === product.image ? 'border-primary-500 ring-2 ring-primary-200' : 'border-background-200/70 hover:border-primary-300'}`}
                >
                  <img alt={`${product.name} principal`} className="w-full h-full object-contain bg-gradient-to-br from-background-100 to-background-200 p-2" src={product.image} />
                </button>
                {product.galleryImages.map((img, idx) => (
                  <button
                    key={img}
                    type="button"
                    onClick={() => { setSelectedImage(img); setLightboxIndex(idx + 1); }}
                    className={`flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-xl border-2 overflow-hidden cursor-pointer transition-all ${selectedImage === img ? 'border-primary-500 ring-2 ring-primary-200' : 'border-background-200/70 hover:border-primary-300'}`}
                  >
                    <img alt={`${product.name} vista ${idx + 1}`} className="w-full h-full object-contain bg-gradient-to-br from-background-100 to-background-200 p-2" src={img} />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-primary-600 text-xs font-bold uppercase tracking-widest mb-2">{product.category}</p>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 uppercase leading-tight mb-4">{product.name}</h1>
            <p className="text-foreground-500 text-sm md:text-base leading-relaxed mb-6">{product.description}</p>

            <div className="grid grid-cols-2 gap-3 mb-6">
              {Object.entries(product.specs).map(([key, value]) => (
                <div key={key} className="flex items-center gap-3 bg-white border border-background-200/70 rounded-xl px-4 py-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-lg bg-primary-50">
                    <i className={`${specIcons[key] || 'ri-settings-line'} text-primary-600 text-base`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-foreground-400 text-[10px] uppercase tracking-wide leading-none mb-1">{specLabels[key] || key}</p>
                    <p className="text-foreground-950 text-sm font-medium truncate">{value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mb-6">
              <p className="text-foreground-400 text-xs uppercase tracking-wide mb-2">Materiais compatíveis</p>
              <div className="flex flex-wrap gap-2">
                {product.compatibilidade.pead && <span className="inline-flex items-center gap-1 text-xs font-bold text-accent-900 bg-accent-100 px-3 py-1.5 rounded-full"><i className="ri-check-line" /> PEAD</span>}
                {product.compatibilidade.pvc && <span className="inline-flex items-center gap-1 text-xs font-bold text-accent-900 bg-accent-100 px-3 py-1.5 rounded-full"><i className="ri-check-line" /> PVC</span>}
                {product.compatibilidade.lldpe && <span className="inline-flex items-center gap-1 text-xs font-bold text-accent-900 bg-accent-100 px-3 py-1.5 rounded-full"><i className="ri-check-line" /> LLDPE</span>}
              </div>
            </div>

            <a
              href={whatsappUrl}
              target="_blank"
              rel="nofollow noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold text-sm px-6 py-4 rounded-full transition-all cursor-pointer whitespace-nowrap uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg" /> Solicitar Orçamento
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-12">
          <div className="bg-white border border-background-200/70 rounded-2xl p-6 md:p-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mb-5 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-primary-500 inline-block" /> Destaques
            </h2>
            <ul className="space-y-3">
              {product.features.map((feature) => (
                <li key={feature} className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full bg-primary-50 mt-0.5">
                    <i className="ri-check-line text-primary-600 text-sm" />
                  </div>
                  <span className="text-foreground-700 text-sm leading-relaxed">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-background-200/70 rounded-2xl p-6 md:p-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mb-5 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-accent-500 inline-block" /> Aplicações recomendadas
            </h2>
            <ul className="space-y-3">
              {product.applications.map((app) => (
                <li key={app} className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full bg-accent-100 mt-0.5">
                    <i className="ri-shield-star-line text-accent-700 text-sm" />
                  </div>
                  <span className="text-foreground-700 text-sm leading-relaxed">{app}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {product.techFeatures && product.techFeatures.length > 0 && (
          <div className="mb-12">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mb-6 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-accent-500 inline-block" /> Características do Produto
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {product.techFeatures.map((tf, idx) => (
                <div key={tf.title} className="bg-white border border-background-200/70 rounded-2xl p-5 md:p-6">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-50 mb-3">
                    <span className="text-accent-700 font-bold text-sm">{String(idx + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950 mb-2">{tf.title}</h3>
                  <p className="text-foreground-600 text-xs md:text-sm leading-relaxed">{tf.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {product.extraSpecs && Object.keys(product.extraSpecs).length > 0 && (
          <div className="mb-12">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mb-6 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-primary-500 inline-block" /> Especificações Técnicas Completas
            </h2>
            <div className="bg-white border border-background-200/70 rounded-2xl overflow-hidden">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {Object.entries(product.extraSpecs).map(([label, value], idx, arr) => (
                  <div
                    key={label}
                    className={`p-4 md:p-5 ${idx < arr.length - 1 ? 'border-b sm:border-b-0 sm:border-r border-background-200/70' : ''}`}
                  >
                    <p className="text-foreground-400 text-[10px] uppercase tracking-wide mb-1">{label}</p>
                    <p className="text-foreground-950 text-sm font-semibold">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {related.length > 0 && (
          <div className="mb-12">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 uppercase mb-6">Outros modelos de {product.category.toLowerCase()}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {related.map((p) => (
                <Link key={p.id} to={`/modelos-de-maquinas/${p.slug}`} className="group bg-white rounded-2xl border border-background-200/70 overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1 hover:border-primary-300 cursor-pointer">
                  <div className="relative w-full h-40 bg-gradient-to-br from-background-100 to-background-200 flex items-center justify-center p-4">
                    {p.badge && <span className="absolute top-3 left-3 bg-primary-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">{p.badge}</span>}
                    <img alt={p.name} className="h-full w-full object-contain" src={p.image} />
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-heading text-lg font-bold text-foreground-950 uppercase">{p.name}</h3>
                      <p className="text-foreground-400 text-xs">{p.specs.potencia}</p>
                    </div>
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 group-hover:bg-primary-500 transition-colors">
                      <i className="ri-arrow-right-line text-primary-600 group-hover:text-white text-sm" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="bg-primary-50 rounded-2xl border border-primary-200/70 p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-lg font-bold text-foreground-950 uppercase mb-1">Tem dúvidas sobre o {product.name}?</h3>
            <p className="text-foreground-500 text-sm">Nossa equipe técnica indica a configuração ideal para o seu projeto.</p>
          </div>
          <a href={whatsappUrl} target="_blank" rel="nofollow noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold text-sm px-8 py-4 rounded-full transition-all cursor-pointer whitespace-nowrap uppercase tracking-wide flex-shrink-0">
            <i className="ri-whatsapp-line" /> Falar com Especialista
          </a>
        </div>
      </div>

      {lightboxIndex !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={() => setLightboxIndex(null)}>
          <button type="button" onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }} className="absolute top-4 right-4 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer" aria-label="Fechar">
            <i className="ri-close-line text-2xl" />
          </button>
          {allImages.length > 1 && (
            <button type="button" onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + allImages.length) % allImages.length); }} className="absolute left-4 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer" aria-label="Anterior">
              <i className="ri-arrow-left-line text-2xl" />
            </button>
          )}
          <img alt={`${product.name} ampliada`} className="max-h-[85vh] max-w-[85vw] object-contain" src={allImages[lightboxIndex]} onClick={(e) => e.stopPropagation()} />
          {allImages.length > 1 && (
            <button type="button" onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % allImages.length); }} className="absolute right-4 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer" aria-label="Próxima">
              <i className="ri-arrow-right-line text-2xl" />
            </button>
          )}
        </div>
      )}
    </Layout>
  );
}